// SVG icons and resource tokens for the HUD (same paths as the canvas icons on the board).
import type { Color } from '@bcm/rules';
import { ICON_PATHS, type IconName } from '../game/icons';
import { EMBLEMS, SEAL_COLORS } from '../game/content';

export function Icon({ name, size = 18, className, title }: { name: IconName; size?: number; className?: string; title?: string }) {
  return (
    <svg className={`icon ${className ?? ''}`} width={size} height={size} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d={ICON_PATHS[name]} fillRule="evenodd" fill="currentColor" />
    </svg>
  );
}

const PERMIT: Record<Color, { a: string; b: string; glyph: string }> = {
  red: { a: '#e8624f', b: '#8a1f1a', glyph: 'M12 5L18.5 17H5.5Z' },
  yellow: { a: '#f5cf5a', b: '#9a6a12', glyph: 'M8.5 5.5H15.5L19 12 15.5 18.5H8.5L5 12Z' },
  blue: { a: '#5f9cf2', b: '#1b3f8a', glyph: 'M12 4.5L19 12 12 19.5 5 12Z' },
  green: { a: '#5cc98a', b: '#1c5a33', glyph: 'M12 5.5A6.5 6.5 0 1 1 12 18.5 6.5 6.5 0 0 1 12 5.5Z' },
};

/** A permit as a little stamped card (Mockup 1's permit tokens): colour + shape. */
export function PermitToken({ color, size = 30 }: { color: Color; size?: number }) {
  const p = PERMIT[color];
  const id = `pt-${color}`;
  return (
    <svg className="permit-token" width={size * 0.78} height={size} viewBox="0 0 19 24" aria-hidden>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={p.a} />
          <stop offset="1" stopColor={p.b} />
        </linearGradient>
      </defs>
      <g transform="rotate(-8 9.5 12)">
        <rect x="1.2" y="1.2" width="16.6" height="21.6" rx="2.6" fill={`url(#${id})`} stroke="#f3dca0" strokeWidth="1" />
        <rect x="3" y="3" width="13" height="18" rx="1.6" fill="none" stroke="rgba(255,240,200,0.45)" strokeWidth="0.6" />
        <path d={p.glyph} transform="translate(0.6 0.6) scale(0.75) translate(0.6 3.4)" fill="#fff6e0" opacity="0.95" />
      </g>
    </svg>
  );
}

export function ManaCrystal({ size = 18, empty = false }: { size?: number; empty?: boolean }) {
  return (
    <svg className={`mana-crystal ${empty ? 'empty' : ''}`} width={size * 0.72} height={size} viewBox="0 0 18 25" aria-hidden>
      <defs>
        <linearGradient id="mc" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffe0f2" />
          <stop offset="0.45" stopColor="#f26bbd" />
          <stop offset="1" stopColor="#8a1f64" />
        </linearGradient>
      </defs>
      <path d="M9 0.8L17 8.5 9 24.2 1 8.5Z" fill={empty ? 'rgba(242,107,189,0.08)' : 'url(#mc)'} stroke={empty ? 'rgba(240,150,205,0.45)' : '#ffd0ec'} strokeWidth="0.8" />
      {!empty && <path d="M9 0.8L6 8.5H12ZM1 8.5H17L9 24.2Z" fill="rgba(255,255,255,0.18)" />}
    </svg>
  );
}

export function MeepleIcon({ color, size = 22, empty = false }: { color: string; size?: number; empty?: boolean }) {
  return (
    <svg className={`meeple-icon ${empty ? 'empty' : ''}`} width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path d={ICON_PATHS.meeple} fill={empty ? 'none' : color} stroke={empty ? 'rgba(201,164,90,0.55)' : 'rgba(0,0,0,0.45)'} strokeWidth="1" />
      {!empty && <path d={ICON_PATHS.meeple} fill="url(#meeple-sheen)" />}
      <defs>
        <linearGradient id="meeple-sheen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="0.5" stopColor="rgba(255,255,255,0)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.25)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** A seat's emblem: its icon on a seal-coloured disc. */
export function SeatEmblem({ seat, size = 24, title }: { seat: number; size?: number; title?: string }) {
  const light = seat === 2; // the cream seal needs dark ink
  return (
    <span className="seat-emblem" style={{ width: size, height: size, background: SEAL_COLORS[seat], color: light ? '#3a2a1a' : '#fff6e0' }} title={title}>
      <Icon name={EMBLEMS[seat] ?? 'quill'} size={Math.round(size * 0.6)} />
    </span>
  );
}
